import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  FocusContext,
  setFocus,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import tts from '../../lib/TTS'
import Seasons from './Seasons'
import SeasonContent from './SeasonContent'
import useSeasons from './useSeasons'
import useSeasonContent from './useSeasonContent'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'
import { getSeasonFullTTS } from './utils'
import googleAnalytics4 from '../../lib/googleAnalytics'

import type { FocusedState } from './types'
import type { Season } from '../../reducers/types/reducerTypes'

const SeasonsScreenTS = () => {
  const [focusedState, setFocusedState] = useState<FocusedState>({
    seasons: undefined,
    seasonContent: undefined,
  })
  const {
    seasons,
    activeSeason,
    setActiveSeason,
    seasonsLoaded,
    showTitle,
    seasonType,
  } = useSeasons()
  const { videoSections, seasonDescription, loading } = useSeasonContent(
    activeSeason,
    seasonType
  )
  const { ref, focusKey } = useFocusable()
  const navigate = useNavigate()

  // Workaround to allow new focus components to work with spatial navigation and old components work with focusable HoC
  // TODO: remove this when whole app is migrated to react spatial navigation
  useEffect(() => {
    window.focusedComponent = {}
    window.focusedComponent.handleBySpatialNavigation = true
    return () => {
      window.focusedComponent.handleBySpatialNavigation = false
    }
  }, [])

  useEffect(() => tts.pushTextToRead(`${showTitle} seasons`), [showTitle])

  useEffect(() => {
    if (!loading && videoSections[0].seasonOrdinal === activeSeason?.ordinal) {
      googleAnalytics4.sendPageViewEvent(
        `Season | ${showTitle} | Season ${activeSeason?.ordinal}`
      )

      tts.stopRead()
      tts.addTextToRead(
        `Focused on Season ${
          activeSeason?.ordinal
        }. ... it has ${getSeasonFullTTS(videoSections)}`
      )
    }
  }, [loading, activeSeason?.ordinal, videoSections])

  useEffect(() => {
    if (
      focusedState.seasons === 'focused' &&
      focusedState.seasonContent === 'blurred'
    ) {
      tts.stopRead()
      tts.addTextToRead(`Focused on Season ${activeSeason?.ordinal}`)
      setFocusedState({ seasons: 'focused', seasonContent: undefined })
    }
  }, [focusedState.seasons, focusedState.seasonContent, activeSeason?.ordinal])

  useKeyDown(KeyTypeEnum.Back, () => {
    navigate(-1)
  })

  useEffect(() => {
    if (seasonsLoaded) {
      setFocus(`${seasonType}-${activeSeason?.cid}`)
    }
  }, [seasonsLoaded, activeSeason?.cid])

  const handleSeasonFocus = (activeSeason: Season) => {
    setActiveSeason(activeSeason)
  }
  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className="seasons-screen">
        <Seasons
          seasons={seasons}
          showTitle={showTitle}
          seasonType={seasonType}
          changeSeasonFocused={handleSeasonFocus}
          onArrowPress={() => !loading}
          loading={loading}
          setFocusedState={setFocusedState}
        />
        <SeasonContent
          videoSections={videoSections}
          activeSeason={activeSeason}
          seasonDescription={seasonDescription}
          loading={loading}
        />
      </div>
    </FocusContext.Provider>
  )
}

export default SeasonsScreenTS
